/**
 * probeSession — the pure half of a Probe call: what the browser knows about
 * the conversation, and how each GPT-Live event changes it.
 *
 * The WebRTC plumbing (`components/regulation/probe/useProbeCall.ts`) is
 * deliberately thin; everything that decides *what the page shows* — the
 * phase, the transcript, which lookup is running — lives here as a reducer
 * over decoded data-channel events, so it can be pinned with fixtures
 * instead of a microphone.
 *
 * GPT-Live is full duplex: both people can speak at once, and transcript
 * fragments for each arrive interleaved, stamped with their place on the
 * session timeline. There is no "turn done" event — so the transcript is
 * built per speaker: a fragment joins that speaker's latest line when it
 * follows it closely on the timeline (`TURN_GAP_MS`) and nobody took the
 * floor in between. The two speakers are not symmetric: any speech from
 * the person ends an examiner line, but only a real examiner line — not a
 * murmur — ends the person's. That is how each "mm-hmm" stays its own
 * sound, riding beside an answer that keeps going instead of cutting it in
 * two (`groupTranscript`).
 *
 * When the examiner looks something up on the web, the pages it read are
 * kept too (`sources`), and drawn as links under the line that used them
 * (`placeSources`) — the voice never reads an address out. A saved
 * interview keeps them under the same turns (`savedTurns`).
 *
 * @module state/probeSession
 */
import {
  LiveServerEvent,
  PROBE_WEB_SEARCH_TOOL,
  ProbeWebSearchOutput,
  ResponsesBackendEvent,
  type LiveServerEvent as LiveServerEventType,
  type ProbeRecord,
} from "@amoreg/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import type { ProbeRecordRead, ProbeToolArguments } from "./probeRecords";

export type ProbePhase = "idle" | "connecting" | "live" | "ended" | "error";

export type ProbeSpeaker = "user" | "assistant";

/** One line of the interview: a speaker's run of speech on the timeline. */
export interface ProbeTranscriptEntry {
  readonly id: string;
  readonly role: ProbeSpeaker;
  readonly text: string;
  /** Session timeline, in milliseconds from the start of the call. */
  readonly startMs: number;
  readonly endMs: number;
  /** Where each fragment began, for telling "spoke in between" from "spoke over". */
  readonly fragmentStarts: ReadonlyArray<number>;
  /** Typed rather than spoken. */
  readonly typed: boolean;
}

export interface ProbeToolCall {
  readonly callId: string;
  readonly name: string;
  readonly arguments: ProbeToolArguments;
  readonly status: "running" | "done" | "failed";
  readonly reason: string | null;
  readonly atMs: number | null;
}

/** A page a web search found, as the transcript links it. */
export interface ProbeSource {
  readonly title: string;
  readonly url: string;
}

/** The pages one web search found, and where the timeline was when they arrived. */
export interface ProbeSourceGroup {
  readonly callId: string;
  readonly atMs: number;
  readonly sources: ReadonlyArray<ProbeSource>;
}

export type ProbeSaveStatus = "unsaved" | "saved" | "failed";

export interface ProbeConversationState {
  readonly phase: ProbePhase;
  readonly sessionId: string | null;
  readonly transcript: ReadonlyArray<ProbeTranscriptEntry>;
  /** Lookups the backend asked for this call, oldest first. */
  readonly tools: ReadonlyArray<ProbeToolCall>;
  /** What each web search found, oldest first. */
  readonly sources: ReadonlyArray<ProbeSourceGroup>;
  readonly records: ReadonlyArray<ProbeRecordRead>;
  /**
   * Delegations still in flight. A delegation whose backend is waiting on
   * our tool results stays open until the continuation settles.
   */
  readonly delegations: ReadonlyArray<{ readonly id: string; readonly awaitingTools: boolean }>;
  /** Why the call ended or failed; a notice while live (moderation, a refused command). */
  readonly error: string | null;
  /** How much the examiner has said since the live notice arrived; null with no notice. */
  readonly noticeHeardMs: number | null;
  /** Connecting, but the browser is still waiting for microphone permission. */
  readonly awaitingMicrophone: boolean;
  readonly save: ProbeSaveStatus;
}

export const initialProbeState: ProbeConversationState = {
  phase: "idle",
  sessionId: null,
  transcript: [],
  tools: [],
  sources: [],
  records: [],
  delegations: [],
  error: null,
  noticeHeardMs: null,
  awaitingMicrophone: false,
  save: "unsaved",
};

/**
 * A pause longer than this ends a speaker's line. Long enough to carry a
 * breath or a "so, um" across; short enough that a new answer is a new line.
 */
export const TURN_GAP_MS = 1_600;

export const NOTICE_HOLD_MS = 4_000;

/** Local (non-network) happenings the hook feeds through the same reducer. */
export type ProbeLocalEvent =
  | { readonly type: "probe.connecting" }
  | { readonly type: "probe.awaiting-microphone" }
  | { readonly type: "probe.microphone-granted" }
  | { readonly type: "probe.ended"; readonly reason?: string }
  | { readonly type: "probe.failed"; readonly message: string }
  | { readonly type: "probe.typed"; readonly text: string }
  | {
      readonly type: "probe.tool.started";
      readonly callId: string;
      readonly name: string;
      readonly arguments: ProbeToolArguments;
    }
  | {
      readonly type: "probe.tool.finished";
      readonly callId: string;
      readonly ok: boolean;
      readonly reason?: string;
      /** The pages a web search found; empty for every other lookup. */
      readonly sources?: ReadonlyArray<ProbeSource>;
      readonly records?: ReadonlyArray<ProbeRecord>;
    }
  | { readonly type: "probe.save.finished"; readonly ok: boolean };

export type ProbeEvent = LiveServerEventType | ProbeLocalEvent;

/** Parse one data-channel message; `None` for anything the page has no use for. */
export const decodeLiveEvent = (raw: string): Option.Option<LiveServerEventType> => {
  let json: unknown;
  try {
    json = JSON.parse(raw) as unknown;
  } catch {
    return Option.none();
  }
  return Schema.decodeUnknownOption(LiveServerEvent)(json);
};

const decodeBackendEvent = Schema.decodeUnknownOption(ResponsesBackendEvent);

/** A function call the backend finished writing, lifted out of its envelope. */
export interface ProbeFunctionCall {
  readonly delegationId: string | null;
  readonly callId: string;
  readonly name: string;
  /** Raw JSON string, exactly as the model produced it. */
  readonly arguments: string;
}

/** The backend event inside a `response.event`, when it is one Probe acts on. */
export const backendEventOf = (event: LiveServerEventType) =>
  event.type === "response.event" ? decodeBackendEvent(event.event) : Option.none();

export const functionCallOf = (event: LiveServerEventType): Option.Option<ProbeFunctionCall> =>
  backendEventOf(event).pipe(
    Option.flatMap((inner) =>
      inner.type === "response.output_item.done"
        ? Option.some({
            delegationId: event.type === "response.event" ? (event.delegation_id ?? null) : null,
            callId: inner.item.call_id,
            name: inner.item.name,
            arguments: inner.item.arguments || "{}",
          })
        : Option.none(),
    ),
  );

/** True when a backend response in this event reached a resting state. */
export const settledDelegationOf = (event: LiveServerEventType): Option.Option<string | null> =>
  backendEventOf(event).pipe(
    Option.flatMap((inner) =>
      inner.type === "response.output_item.done"
        ? Option.none()
        : Option.some(event.type === "response.event" ? (event.delegation_id ?? null) : null),
    ),
  );

/**
 * Join a fragment onto a line. Fragments carry their own leading space
 * mid-sentence ("Hello," + " I'm"), but the first fragment after a pause
 * does not ("one second." + "Right.") — and a word can be split across two
 * ("Mm-h" + "mm."). So a space goes in only after a finished sentence.
 */
export const joinFragment = (text: string, delta: string): string =>
  /[.!?…]["')\]]?$/.test(text) && /^[\p{L}\p{N}"'(]/u.test(delta)
    ? `${text} ${delta}`
    : text + delta;

/** The longest listening sound: "mm-hmm", "right, yeah", "okay then". */
const MURMUR_MAX_WORDS = 3;
const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

/** A line's sentences, without their closing punctuation: "Mm-hmm. Right." → ["Mm-hmm", "Right"]. */
export const murmursOf = (text: string): ReadonlyArray<string> =>
  text
    .split(/[.!?…]+/)
    .map((piece) => piece.replace(/^[\s,;:]+|[\s,;:]+$/g, ""))
    .filter((piece) => piece.length > 0);

/**
 * The words a listener murmurs: hums in any spelling ("mm-hmm", "mhm",
 * "hmm", "uh-huh") and the short acknowledgements. A short line is only a
 * murmur when it is made of these — "Please stop now." is short, and a turn.
 */
const LISTENING_WORD =
  /^(?:[mh]+(?:-[mh]+)*|u+h*-?hu+h*|ah|oh|right|yeah|yes|yep|okay|ok|sure|good|great|alright|fine|understood|go|on|i|see|got|it|mm)$/;

/** Only listening sounds: every sentence a word or three, every word a listening one. */
export const isMurmur = (text: string): boolean => {
  const pieces = murmursOf(text);
  return (
    pieces.length > 0 &&
    pieces.every(
      (piece) =>
        wordCount(piece) <= MURMUR_MAX_WORDS &&
        piece
          .toLowerCase()
          .split(/[\s,]+/)
          .filter(Boolean)
          .every((word) => LISTENING_WORD.test(word)),
    )
  );
};

/**
 * Did the other speaker take the floor between a line and its next
 * fragment — that is, start saying something in the gap? Speech that began
 * earlier and ran over it does not count: that is talking at the same time,
 * not taking turns. For the examiner, anything the person starts counts;
 * for the person, only a real examiner line does, never a murmur.
 */
const flooredBetween = (
  transcript: ReadonlyArray<ProbeTranscriptEntry>,
  line: ProbeTranscriptEntry,
  nextStartMs: number,
): boolean =>
  transcript.some(
    (other) =>
      other.role !== line.role &&
      (line.role === "assistant" || !isMurmur(other.text)) &&
      other.fragmentStarts.some((start) => start >= line.endMs && start < nextStartMs),
  );

/**
 * Add a fragment to its speaker's line, or start a new one. Lines are kept
 * in timeline order by start, so a backchannel lands where it was said.
 */
export const appendFragment = (
  transcript: ReadonlyArray<ProbeTranscriptEntry>,
  fragment: {
    readonly role: ProbeSpeaker;
    readonly delta: string;
    readonly startMs: number;
    readonly endMs: number;
  },
): ReadonlyArray<ProbeTranscriptEntry> => {
  const latest = transcript.findLast((entry) => entry.role === fragment.role && !entry.typed);
  if (
    latest !== undefined &&
    fragment.startMs - latest.endMs <= TURN_GAP_MS &&
    !flooredBetween(transcript, latest, fragment.startMs)
  ) {
    return transcript.map((entry) =>
      entry === latest
        ? {
            ...entry,
            text: joinFragment(entry.text, fragment.delta),
            endMs: Math.max(entry.endMs, fragment.endMs),
            fragmentStarts: [...entry.fragmentStarts, fragment.startMs],
          }
        : entry,
    );
  }
  const line: ProbeTranscriptEntry = {
    id: `${fragment.role}-${fragment.startMs}-${transcript.length}`,
    role: fragment.role,
    // A line cannot open on the tail of the last one's punctuation.
    text: fragment.delta.replace(/^[\s.,;:!?…]+/, ""),
    startMs: fragment.startMs,
    endMs: fragment.endMs,
    fragmentStarts: [fragment.startMs],
    typed: false,
  };
  const at = transcript.findIndex((entry) => entry.startMs > line.startMs);
  return at === -1
    ? [...transcript, line]
    : [...transcript.slice(0, at), line, ...transcript.slice(at)];
};

const decodeWebSearchOutput = Schema.decodeUnknownOption(ProbeWebSearchOutput);

/**
 * A page as the transcript links it, or null when its address is not an
 * http(s) one — the page renders sources as links, live and in the history,
 * so anything else is never drawn. A page with no title goes by its host.
 */
export const linkableSource = (title: string, address: string): ProbeSource | null => {
  try {
    const url = new URL(address);
    return url.protocol === "https:" || url.protocol === "http:"
      ? { title: title.trim() || url.hostname, url: url.href }
      : null;
  } catch {
    return null;
  }
};

/**
 * The pages in a tool's output, when the tool was the web search and it
 * answered; nothing for any other tool or a refusal.
 */
export const webSourcesOf = (name: string, output: unknown): ReadonlyArray<ProbeSource> => {
  if (name !== PROBE_WEB_SEARCH_TOOL) return [];
  return Option.match(decodeWebSearchOutput(output), {
    onNone: () => [],
    onSome: ({ results }) =>
      results.flatMap((result) => linkableSource(result.title, result.url) ?? []),
  });
};

/** Where the timeline has got to, for placing a typed answer after it. */
const timelineEnd = (transcript: ReadonlyArray<ProbeTranscriptEntry>): number =>
  transcript.reduce((end, entry) => Math.max(end, entry.endMs), 0);

const finishTool = (
  tools: ReadonlyArray<ProbeToolCall>,
  callId: string,
  ok: boolean,
  reason: string | undefined,
  atMs: number,
): ReadonlyArray<ProbeToolCall> =>
  tools.map((tool) =>
    tool.callId === callId
      ? { ...tool, status: ok ? "done" : "failed", reason: ok ? null : (reason ?? null), atMs }
      : tool,
  );

const closedReason = (reason: string | undefined): string | null => {
  switch (reason) {
    case "expired":
      return "The call reached its time limit.";
    case "content":
      return "The call was ended by OpenAI's safety filter.";
    case "connection_lost":
      return "The connection dropped.";
    default:
      return null;
  }
};

const isOver = (state: ProbeConversationState) =>
  state.phase === "ended" || state.phase === "error";

export const CALL_ENDED_REASON = "The call ended before this finished.";

const failRunningLookups = (
  tools: ReadonlyArray<ProbeToolCall>,
  atMs: number,
): ReadonlyArray<ProbeToolCall> =>
  tools.some((tool) => tool.status === "running")
    ? tools.map((tool) =>
        tool.status === "running"
          ? { ...tool, status: "failed", reason: CALL_ENDED_REASON, atMs }
          : tool,
      )
    : tools;

const endCall = (
  state: ProbeConversationState,
  phase: "ended" | "error",
  error: string | null,
): ProbeConversationState => ({
  ...state,
  phase,
  error,
  noticeHeardMs: null,
  tools: failRunningLookups(state.tools, timelineEnd(state.transcript)),
});

/**
 * A live notice stays until the examiner has spoken `NOTICE_HOLD_MS` since it
 * arrived. Measured in speech, not timeline: the timeline stands still through
 * a silence, so a notice that lands after one would otherwise clear on the
 * examiner's first word.
 */
const noticeHeard = (
  state: ProbeConversationState,
  event: { readonly start_ms: number; readonly end_ms: number },
): number | null =>
  state.noticeHeardMs === null || state.phase !== "live"
    ? state.noticeHeardMs
    : state.noticeHeardMs + Math.max(0, event.end_ms - event.start_ms);

/**
 * One step of the conversation. Events the page does not understand never
 * reach here — `decodeLiveEvent` drops them at the edge.
 */
export const reduceProbeEvent = (
  state: ProbeConversationState,
  event: ProbeEvent,
): ProbeConversationState => {
  switch (event.type) {
    case "probe.connecting":
      return { ...initialProbeState, phase: "connecting" };
    case "probe.awaiting-microphone":
      return state.phase === "connecting" ? { ...state, awaitingMicrophone: true } : state;
    case "probe.microphone-granted":
      return { ...state, awaitingMicrophone: false };
    case "probe.ended":
      return isOver(state) ? state : endCall(state, "ended", closedReason(event.reason));
    case "probe.failed":
      return endCall(state, "error", event.message || "The call failed.");
    case "probe.typed": {
      const text = event.text.trim();
      if (text.length === 0) return state;
      const at = timelineEnd(state.transcript) + 1;
      return {
        ...state,
        transcript: [
          ...state.transcript,
          {
            id: `typed-${at}`,
            role: "user",
            text,
            startMs: at,
            endMs: at,
            fragmentStarts: [at],
            typed: true,
          },
        ],
      };
    }
    case "probe.tool.started":
      return {
        ...state,
        tools: [
          ...state.tools,
          {
            callId: event.callId,
            name: event.name,
            arguments: event.arguments,
            status: "running",
            reason: null,
            atMs: null,
          },
        ],
      };
    case "probe.tool.finished": {
      // The call's end already settled every running lookup; a late answer must not reopen it.
      if (state.phase === "ended" || state.phase === "error") return state;
      const atMs = timelineEnd(state.transcript);
      const tools = finishTool(state.tools, event.callId, event.ok, event.reason, atMs);
      const sources = event.sources ?? [];
      const records = event.records ?? [];
      if (sources.length === 0 && records.length === 0) return { ...state, tools };
      return {
        ...state,
        tools,
        sources:
          sources.length === 0
            ? state.sources
            : [...state.sources, { callId: event.callId, atMs, sources }],
        records:
          records.length === 0
            ? state.records
            : [
                ...state.records,
                ...records.map((record) => ({ callId: event.callId, atMs, record })),
              ],
      };
    }
    case "probe.save.finished":
      return { ...state, save: event.ok ? "saved" : "failed" };

    case "session.started":
      return isOver(state)
        ? state
        : {
            ...state,
            phase: "live",
            sessionId: event.session.id,
            error: null,
            noticeHeardMs: null,
          };

    case "session.input_transcript.delta":
    case "session.output_transcript.delta": {
      if (event.delta.length === 0) return state;
      const heard =
        event.type === "session.output_transcript.delta"
          ? noticeHeard(state, event)
          : state.noticeHeardMs;
      const cleared = heard !== null && heard >= NOTICE_HOLD_MS;
      return {
        ...state,
        error: cleared ? null : state.error,
        noticeHeardMs: cleared ? null : heard,
        transcript: appendFragment(state.transcript, {
          role: event.type === "session.input_transcript.delta" ? "user" : "assistant",
          delta: event.delta,
          startMs: event.start_ms,
          endMs: event.end_ms,
        }),
      };
    }

    case "session.delegation.created":
      return {
        ...state,
        delegations: [...state.delegations, { id: event.delegation.id, awaitingTools: false }],
      };

    case "response.event": {
      const id = event.delegation_id ?? null;
      if (Option.isSome(functionCallOf(event))) {
        return {
          ...state,
          delegations: state.delegations.map((open) =>
            open.id === id ? { ...open, awaitingTools: true } : open,
          ),
        };
      }
      if (Option.isNone(settledDelegationOf(event))) return state;
      // A response that stopped for our tools is followed by a continuation;
      // the delegation is done when a response settles with nothing owed.
      return {
        ...state,
        delegations: state.delegations.flatMap((open) =>
          open.id !== id ? [open] : open.awaitingTools ? [{ ...open, awaitingTools: false }] : [],
        ),
      };
    }

    case "session.closed":
      return isOver(state) ? state : endCall(state, "ended", closedReason(event.reason));

    case "error":
      // GPT-Live keeps the session up through most errors — a refused
      // command, a moderation cut of one reply — so a live call shows it
      // as a notice and carries on. Before the session starts it is fatal.
      return state.phase === "live"
        ? { ...state, error: event.error.message, noticeHeardMs: 0 }
        : endCall(state, "error", event.error.message || "The examiner reported an error.");
  }
};

// ── Presentation ───────────────────────────────────────────────────────

/**
 * A line of the interview as the page draws it. A backchannel — a short
 * examiner line said while the person kept talking — is not a line of its
 * own; it rides on the answer it was said over.
 */
export type ProbeTranscriptItem =
  | {
      readonly kind: "answer";
      readonly entry: ProbeTranscriptEntry;
      readonly backchannels: ReadonlyArray<string>;
    }
  | { readonly kind: "examiner"; readonly entry: ProbeTranscriptEntry };

export const isBackchannel = (
  entry: ProbeTranscriptEntry,
  transcript: ReadonlyArray<ProbeTranscriptEntry>,
): boolean =>
  entry.role === "assistant" &&
  isMurmur(entry.text) &&
  transcript.some(
    (other) =>
      other.role === "user" && other.startMs <= entry.startMs && other.endMs >= entry.endMs,
  );

export const groupTranscript = (
  transcript: ReadonlyArray<ProbeTranscriptEntry>,
): ReadonlyArray<ProbeTranscriptItem> => {
  const items: Array<ProbeTranscriptItem> = [];
  for (const entry of transcript) {
    if (entry.text.trim().length === 0) continue;
    if (entry.role === "user") {
      items.push({ kind: "answer", entry, backchannels: [] });
      continue;
    }
    if (isBackchannel(entry, transcript)) {
      const answer = items.findLast(
        (item): item is Extract<ProbeTranscriptItem, { kind: "answer" }> =>
          item.kind === "answer" &&
          item.entry.startMs <= entry.startMs &&
          item.entry.endMs >= entry.endMs,
      );
      if (answer !== undefined) {
        items[items.indexOf(answer)] = {
          ...answer,
          backchannels: [...answer.backchannels, ...murmursOf(entry.text)],
        };
        continue;
      }
    }
    items.push({ kind: "examiner", entry });
  }
  return items;
};

const lineAt = (
  items: ReadonlyArray<ProbeTranscriptItem>,
  atMs: number,
): ProbeTranscriptItem | undefined =>
  items.find((candidate) => candidate.kind === "examiner" && candidate.entry.startMs >= atMs) ??
  items.at(-1);

/** Which line each arrival belongs under, by entry id: the first examiner line at or after it, else the latest line. */
export const byLineAt = <T extends { readonly atMs: number }>(
  items: ReadonlyArray<ProbeTranscriptItem>,
  arrivals: ReadonlyArray<T>,
): Map<string, Array<T>> => {
  const byLine = new Map<string, Array<T>>();
  for (const arrival of arrivals) {
    const item = lineAt(items, arrival.atMs);
    if (item === undefined) continue;
    byLine.set(item.entry.id, [...(byLine.get(item.entry.id) ?? []), arrival]);
  }
  return byLine;
};

/**
 * Which line each web search's pages belong under, by entry id: the first
 * examiner line spoken after the pages arrived — the question they informed
 * — or, until the examiner speaks again, the latest line.
 *
 * Several searches often inform one line (the examiner checks the
 * regulator's site, then the wider web), so a line's pages are interleaved
 * by rank — every search's best match first, then every second-best — not
 * one search's tail before the next search's best. A page found twice for
 * one line is linked once, where it ranked highest.
 */
export const placeSources = (
  items: ReadonlyArray<ProbeTranscriptItem>,
  groups: ReadonlyArray<ProbeSourceGroup>,
): ReadonlyMap<string, ReadonlyArray<ProbeSource>> => {
  const byLine = new Map(
    [...byLineAt(items, groups)].map(([id, placed]) => [id, placed.map((group) => group.sources)]),
  );
  const placed = new Map<string, ReadonlyArray<ProbeSource>>();
  for (const [id, searches] of byLine) {
    const kept: Array<ProbeSource> = [];
    const deepest = Math.max(...searches.map((sources) => sources.length));
    for (let rank = 0; rank < deepest; rank++) {
      for (const sources of searches) {
        const source = sources[rank];
        if (source !== undefined && !kept.some((other) => other.url === source.url))
          kept.push(source);
      }
    }
    placed.set(id, kept);
  }
  return placed;
};

export interface ProbeFinishedLookup {
  readonly name: string;
  readonly arguments: ProbeToolArguments;
  readonly status: "done" | "failed";
  readonly reason: string | null;
  readonly atMs: number;
  readonly records: ReadonlyArray<ProbeRecord>;
}

export const finishedLookups = (
  state: Pick<ProbeConversationState, "tools" | "records">,
): ReadonlyArray<ProbeFinishedLookup> =>
  state.tools.flatMap(({ callId, name, arguments: args, status, reason, atMs }) =>
    status === "running" || atMs === null
      ? []
      : [
          {
            name,
            arguments: args,
            status,
            reason,
            atMs,
            records: state.records.flatMap((read) => (read.callId === callId ? [read.record] : [])),
          },
        ],
  );

export interface ProbeSavedTurn {
  readonly role: ProbeSpeaker;
  readonly text: string;
  readonly sources?: ReadonlyArray<ProbeSource>;
  readonly lookups?: ReadonlyArray<ProbeFinishedLookup>;
}

/**
 * The turns worth keeping: answers and the examiner's lines, without the
 * murmurs — each with the pages the transcript linked under it
 * (`placeSources`), so the history shows them where the call did.
 */
export const savedTurns = (
  transcript: ReadonlyArray<ProbeTranscriptEntry>,
  groups: ReadonlyArray<ProbeSourceGroup> = [],
  lookups: ReadonlyArray<ProbeFinishedLookup> = [],
): ReadonlyArray<ProbeSavedTurn> => {
  const items = groupTranscript(transcript);
  const placed = placeSources(items, groups);
  const read = byLineAt(items, lookups);
  return items.map((item) => {
    const role = item.kind === "answer" ? ("user" as const) : ("assistant" as const);
    const text = item.entry.text.trim();
    const sources = placed.get(item.entry.id) ?? [];
    const finished = read.get(item.entry.id) ?? [];
    return {
      role,
      text,
      ...(sources.length === 0 ? {} : { sources }),
      ...(finished.length === 0 ? {} : { lookups: finished }),
    };
  });
};

/** What the examiner is doing, for the line under the orb. */
export type ProbeActivity = "listening" | "speaking";

export const describeProbePhase = (
  state: ProbeConversationState,
  activity: ProbeActivity,
): string => {
  switch (state.phase) {
    case "idle":
      return "Press the orb to begin the interview";
    case "connecting":
      return state.awaitingMicrophone ? "Allow the microphone to start…" : "Connecting…";
    case "ended":
      return state.error ?? "Call ended";
    case "error":
      return state.error ?? "Something went wrong";
    case "live": {
      const running = state.tools.find((tool) => tool.status === "running");
      if (running) return `Checking ${describeToolName(running.name)}…`;
      if (activity === "speaking") return "Speaking";
      if (state.delegations.length > 0) return "Looking into it…";
      return "Listening";
    }
  }
};

export const describeProbeNotice = (state: ProbeConversationState): string | null => {
  if (state.phase === "live") return state.error;
  return isOver(state) && state.save === "failed" ? "This interview couldn't be saved." : null;
};

/** `governance_list_regulatory_updates` → "regulatory updates". */
export const describeToolName = (name: string): string => {
  if (name === PROBE_WEB_SEARCH_TOOL) return "the web";
  const stripped = name.replace(/^governance_/, "").replace(/_/g, " ");
  return stripped
    .replace(/^list /, "")
    .replace(/^get policy text$/, "the document text")
    .replace(/^get policy$/, "the policy")
    .replace(/^search knowledge$/, "the knowledge base")
    .replace(/^get probe interview$/, "a past rehearsal")
    .replace(/^probe interviews$/, "past rehearsals");
};
